import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Search, Loader2, Globe, Twitter, MessageCircle, ExternalLink, X, ArrowLeft, Copy, Check } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { fetchTokenMeta, type TokenMeta } from "@/lib/tokenMeta";
import { TokenAvatar } from "@/components/TokenAvatar";
import { PrebondRow, BondedRow } from "@/components/NurseryPanel";
import * as nursery from "@/lib/nurseryEngine";
import { looksLikeSolanaAddress } from "@/lib/solanaBalance";
import { WalletIntelligenceView } from "@/components/WalletIntelligenceView";
import { prefetchCandles } from "@/lib/pumpCandles";

interface SearchResult {
  mint: string;
  name: string;
  symbol: string;
  image: string;
  mcap: number;
  priceUsd: number;
  priceSol: number;
  source: string;
  liquidityUsd?: number;
  volume24h?: number;
  holderCount?: number;
  ath?: number;
  spawnMc?: number;
  priceChange24h?: number;
  multiplier?: number;
  description?: string;
}

export function SpotlightSearch({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { setCaMintInput, searchHistory, addToSearchHistory, removeFromSearchHistory, launchProviderFilter } = useApp() as any;
  const { t } = useTranslation();
  
  const getMultiplierVal = (coin: SearchResult) => {
    return coin.multiplier || (coin.spawnMc && coin.spawnMc > 0 && coin.mcap ? coin.mcap / coin.spawnMc : 0);
  };
  
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isWalletQuery, setIsWalletQuery] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Coin details states
  const [selectedCoin, setSelectedCoin] = useState<SearchResult | null>(null);
  const [coinMeta, setCoinMeta] = useState<TokenMeta | null>(null);
  const [isLoadingMeta, setIsLoadingMeta] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedMint, setCopiedMint] = useState<string | null>(null);
  const [addressTokenMeta, setAddressTokenMeta] = useState<any | null>(null);
  const [isLoadingAddressMeta, setIsLoadingAddressMeta] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  // Focus input on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setSearchQuery("");
      setResults([]);
      setIsWalletQuery(false);
      setSelectedCoin(null);
      setCoinMeta(null);
      setActiveIndex(0);
    }
  }, [isOpen]);

  // Handle global shortcuts (Esc to close)
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Debounced API search
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < 1) {
      setResults([]);
      setIsSearching(false);
      setIsWalletQuery(false);
      return;
    }

    if (looksLikeSolanaAddress(trimmed)) {
      setIsWalletQuery(true);
      setIsSearching(false);
      // Also fetch token meta from pump.fun for this address as it could be a token mint
      setAddressTokenMeta(null);
      setIsLoadingAddressMeta(true);
      fetch(`/api/server-paper-open-positions`) // use existing infra; fall back to pump directly
        .then(() => fetch(`/pump-frontend/coins/${trimmed}`))
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data && data.mint) {
            setAddressTokenMeta(data);
          } else {
            setAddressTokenMeta(null);
          }
        })
        .catch(() => setAddressTokenMeta(null))
        .finally(() => setIsLoadingAddressMeta(false));
      // Also run the regular search so corpus results populate too
    } else {
      setIsWalletQuery(false);
      setAddressTokenMeta(null);
    }

    let abortController = new AbortController();

    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(trimmed)}`, {
          signal: abortController.signal
        });
        
        if (res.ok && res.body) {
          const reader = res.body.getReader();
          const decoder = new TextDecoder("utf-8");
          let buffer = "";
          
          const resultMap = new Map<string, SearchResult>();

          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            
            buffer += decoder.decode(value, { stream: true });
            
            const lines = buffer.split('\n');
            buffer = lines.pop() || "";
            
            let updated = false;
            for (const line of lines) {
              if (line.trim()) {
                try {
                  const data = JSON.parse(line);
                  if (data.chunk && Array.isArray(data.chunk)) {
                    data.chunk.forEach((coin: SearchResult) => {
                      resultMap.set(coin.mint, coin);
                    });
                    updated = true;
                  }
                } catch (e) {
                  // Ignore parse errors on partial chunks if any
                }
              }
            }
            
            if (updated) {
              const isPumpAllowed = launchProviderFilter?.includes("Pump.fun") ?? true;
              let currentList = Array.from(resultMap.values());
              
              if (!isPumpAllowed) {
                currentList = currentList.filter(c => !JSON.stringify(c).toLowerCase().includes("pump"));
              }
              
              currentList.sort((a, b) => {
                return (b.mcap || 0) - (a.mcap || 0);
              });
              setResults(currentList);
              setActiveIndex(0);
            }
          }
        }
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error("Search fetch error", err);
        }
      } finally {
        setIsSearching(false);
      }
    }, 200);

    return () => {
      clearTimeout(timer);
      abortController.abort();
    };
  }, [searchQuery]);

  // Fetch token details when selected
  const handleSelectCoin = async (coin: SearchResult) => {
    addToSearchHistory(coin);
    setSelectedCoin(coin);
    setIsLoadingMeta(true);
    setCoinMeta(null);
    try {
      const meta = await fetchTokenMeta(coin.mint);
      setCoinMeta(meta);
    } catch {
      // fallback
    } finally {
      setIsLoadingMeta(false);
    }
  };

  // Keyboard navigation inside list
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (selectedCoin) return; // Disregard list navigation if viewing details

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[activeIndex]) {
        handleSelectCoin(results[activeIndex]!);
      }
    }
  };

  // Scroll active item into view
  useEffect(() => {
    if (resultsContainerRef.current) {
      const activeEl = resultsContainerRef.current.children[activeIndex] as HTMLElement;
      if (activeEl) {
        const container = resultsContainerRef.current;
        const top = activeEl.offsetTop;
        const bottom = top + activeEl.offsetHeight;
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.offsetHeight;

        if (top < containerTop) {
          container.scrollTop = top;
        } else if (bottom > containerBottom) {
          container.scrollTop = bottom - container.offsetHeight;
        }
      }
    }
  }, [activeIndex]);

  const handleOpen = (mint: string, path: string) => {
    setCaMintInput(mint);
    if (window.location.pathname !== path) {
      window.history.pushState({}, "", path + window.location.search);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
    onClose();
  };

  const handleCopy = (txt: string) => {
    navigator.clipboard.writeText(txt).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderNurseryStyleRow = (coin: SearchResult, idx: number, handleSelect: (c: SearchResult) => void) => {
    const isHighlighted = idx === activeIndex;
    
    // 1. Try to find it in the live nursery snapshot!
    const snap = nursery.getSnapshot();
    let livePrebondToken = snap.visibleTokens.find((t: any) => t.mint === coin.mint) || snap.visibleTokens.find((t: any) => t.mint === coin.mint);
    if (livePrebondToken) {
      return (
        <div key={coin.mint} onMouseEnter={() => { setActiveIndex(idx); prefetchCandles(coin.mint, "1s"); }} className="w-full">
          <PrebondRow token={livePrebondToken} showProgress={true} isSelected={isHighlighted} onOpen={() => handleSelect(coin)} />
        </div>
      );
    }
    
    let liveBondedToken = snap.visibleTokens.find((t: any) => t.mint === coin.mint);
    if (liveBondedToken) {
      return (
        <div key={coin.mint} onMouseEnter={() => setActiveIndex(idx)} className="w-full">
          <BondedRow token={liveBondedToken} showScore={true} isSelected={isHighlighted} onOpen={() => handleSelect(coin)} />
        </div>
      );
    }

    // 2. Fallback to mock token for non-live search results
    const mockToken = {
      mint: coin.mint,
      name: coin.name,
      symbol: coin.symbol,
      imageUri: coin.image,
      marketCapSol: coin.mcap ? coin.mcap / 150 : 0,
      marketCapUsd: coin.mcap || 0,
      buys5m: 0,
      sells5m: 0,
      peakMcSol: coin.ath ? coin.ath / 150 : 0,
      vol5mSol: 0,
      momentumBurst: false,
      phantomTrade: null,
      firstSeenMs: Date.now() - 3600000,
      createdAt: Date.now() - 3600000,
      creator: "",
      isPresaved: false,
      traderCount: coin.holderCount || 0,
      maxSingleBuySol: 0,
      consecutiveBuys: 0,
      largeTradeCluster: 0,
      runnerScore: undefined,
      multiplier: coin.multiplier
    };

    return (
      <div key={coin.mint} onMouseEnter={() => setActiveIndex(idx)} className="w-full">
        {mockToken.marketCapUsd > 12000 ? (
          <BondedRow token={mockToken} showScore={false} isSelected={isHighlighted} onOpen={() => handleSelect(coin)} />
        ) : (
          <PrebondRow token={mockToken} showProgress={false} isSelected={isHighlighted} onOpen={() => handleSelect(coin)} />
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[99990] flex items-start justify-center pt-[12vh] p-4 pointer-events-auto">
      {/* Click outside to close */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Spotlight Panel */}
      <div className="relative w-full max-w-2xl bg-[#0b0e14]/95 border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200 flex flex-col max-h-[70vh] text-white">
        
        {/* Search header or details back-bar */}
        {!selectedCoin ? (
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/10 shrink-0">
            <Search className="size-5 text-white/40 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder={t("search.placeholder")}
              className="flex-1 bg-transparent border-none outline-none text-sm placeholder:text-white/30 text-white"
            />
            {isSearching ? (
              <Loader2 className="size-4 text-emerald-400 animate-spin shrink-0" />
            ) : searchQuery ? (
              <button onClick={() => setSearchQuery("")} className="text-white/40 hover:text-white">
                <X className="size-4" />
              </button>
            ) : null}
            <span className="hidden sm:inline-block px-1.5 py-0.5 rounded bg-white/5 text-[9px] font-mono text-white/30 border border-white/5 uppercase select-none">ESC</span>
          </div>
        ) : (
          <div className="flex items-center justify-between px-4 py-3.5 border-b border-white/10 shrink-0 bg-white/[0.02]">
            <button
              onClick={() => setSelectedCoin(null)}
              className="flex items-center gap-1 text-xs text-white/60 hover:text-white transition-colors"
            >
              <ArrowLeft className="size-4" />
              <span>{t("nav.dashboard")} / Search</span>
            </button>
            <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
              <X className="size-4" />
            </button>
          </div>
        )}

        {/* Scrollable body */}
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
          {isWalletQuery ? (
            // ── Solana Address View: token + wallet intelligence side by side ──
            <div className="divide-y divide-white/5">
              {/* Section 1: Token Match (corpus + pump) */}
              {(isSearching || isLoadingAddressMeta || results.length > 0 || addressTokenMeta) && (
                <div className="p-3 space-y-2">
                  <div className="px-2 text-[9px] font-bold text-white/30 uppercase tracking-widest flex items-center gap-2">
                    <Search className="size-3" /> Token Match
                    {(isSearching || isLoadingAddressMeta) && <Loader2 className="size-3 animate-spin text-emerald-400" />}
                  </div>
                  {/* Corpus results */}
                  {results.length > 0 && (
                    <div className="space-y-1">
                      {results.slice(0, 3).map((coin, idx) => renderNurseryStyleRow(coin, idx, (c) => {
                        addToSearchHistory(c);
                        setCaMintInput(c.mint);
                        onClose();
                      }))}
                    </div>
                  )}
                  {/* Live pump.fun token meta */}
                  {addressTokenMeta && (
                    <button
                      onClick={() => { setCaMintInput(addressTokenMeta.mint); addToSearchHistory({ mint: addressTokenMeta.mint, name: addressTokenMeta.name, symbol: addressTokenMeta.symbol, image: addressTokenMeta.image_uri, mcap: (addressTokenMeta.market_cap || 0) * 150, priceUsd: 0, priceSol: 0, source: 'pump' }); onClose(); }}
                      onMouseEnter={() => prefetchCandles(addressTokenMeta.mint, "1s")}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 rounded-xl transition-colors text-left"
                    >
                      {addressTokenMeta.image_uri ? (
                        <img src={addressTokenMeta.image_uri} className="size-10 rounded-full border border-white/10 shrink-0" alt={addressTokenMeta.symbol} />
                      ) : (
                        <div className="size-10 rounded-full bg-white/5 border border-white/10 shrink-0 flex items-center justify-center text-white/30 text-xs">{addressTokenMeta.symbol?.slice(0,2)}</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-white text-sm truncate">{addressTokenMeta.name}</span>
                          <span className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">${addressTokenMeta.symbol}</span>
                          <span className="text-[9px] text-white/30 bg-blue-500/10 px-1.5 py-0.5 rounded">pump.fun</span>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 text-[10px] text-white/40 font-mono">
                          <span>MC: ${((addressTokenMeta.market_cap || 0) * 150).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                          <span>{addressTokenMeta.reply_count || 0} replies</span>
                          {addressTokenMeta.twitter && <span className="text-sky-400">@twitter</span>}
                        </div>
                      </div>
                      <ExternalLink className="size-3.5 text-white/20 shrink-0" />
                    </button>
                  )}
                  {!isSearching && !isLoadingAddressMeta && results.length === 0 && !addressTokenMeta && (
                    <div className="px-3 py-2 text-[11px] text-white/30 font-mono">No token found in corpus or pump.fun</div>
                  )}
                </div>
              )}
              {/* Section 2: Wallet Intelligence */}
              <div>
                <div className="px-5 pt-3 pb-1 text-[9px] font-bold text-white/30 uppercase tracking-widest flex items-center gap-2">
                  <Search className="size-3" /> Wallet Intelligence
                </div>
                <WalletIntelligenceView address={searchQuery.trim()} />
              </div>
            </div>
          ) : !selectedCoin ? (
            // Search results listing
            searchQuery.trim().length === 0 ? (
              searchHistory.length > 0 ? (
                <div className="p-3 space-y-3">
                  <div className="px-2 text-[10px] font-bold text-white/40 uppercase tracking-widest flex items-center gap-2">
                    <Search className="size-3" /> {t("search.recentSearches")}
                  </div>
                  <div className="space-y-1">
                    {searchHistory.map((coin: SearchResult, idx: number) => (
                      <div key={coin.mint} className="relative group">
                        {renderNurseryStyleRow(coin, idx, handleSelectCoin)}
                        <div className="absolute right-4 top-3.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(coin.mint);
                              setCopiedMint(coin.mint);
                              setTimeout(() => setCopiedMint(null), 2000);
                            }}
                            className="p-1.5 hover:bg-white/10 rounded-full text-white/30 hover:text-white transition-all shadow-lg bg-black/60 border border-white/10"
                            title="Copy Contract Address"
                          >
                            {copiedMint === coin.mint ? <Check className="size-3.5 text-emerald-400" /> : <Copy className="size-3.5" />}
                          </button>
                          <button 
                            onClick={(e) => { e.stopPropagation(); removeFromSearchHistory(coin.mint); }}
                            className="p-1.5 hover:bg-rose-500/20 rounded-full text-white/30 hover:text-rose-400 transition-all shadow-lg bg-black/60 border border-white/10"
                            title="Remove from history"
                          >
                            <X className="size-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center text-white/30 space-y-3 font-mono text-xs">
                  <div className="text-white/40">Enter token name, ticker symbol, or mint address to start search...</div>
                  <div className="flex flex-wrap gap-2 justify-center pt-2">
                    <span className="px-2 py-1 rounded bg-white/5 border border-white/5 text-[10px]">⌘K to search</span>
                    <span className="px-2 py-1 rounded bg-white/5 border border-white/5 text-[10px]">Arrow keys to navigate</span>
                    <span className="px-2 py-1 rounded bg-white/5 border border-white/5 text-[10px]">Enter to inspect details</span>
                  </div>
                </div>
              )
            ) : results.length === 0 && !isSearching ? (
              <div className="p-12 text-center text-white/30 font-mono text-xs">
                No matching tokens found.
              </div>
            ) : (
              <div ref={resultsContainerRef} className="space-y-0 pb-12">
                {results.map((coin, idx) => renderNurseryStyleRow(coin, idx, handleSelectCoin))}
              </div>
            )
          ) : (
            // Coin profile modal details
            <div className="p-5 space-y-6">
              
              {/* Top Meta header */}
              <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
                <TokenAvatar
                  mint={selectedCoin.mint}
                  symbol={selectedCoin.symbol}
                  name={selectedCoin.name}
                  imageUri={selectedCoin.image}
                  size="size-20 sm:size-24"
                  animated={true}
                />
                <div className="flex-1 space-y-1">
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                    <h2 className="text-xl font-bold">{selectedCoin.name}</h2>
                    <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 text-[10px] font-bold rounded uppercase">
                      {selectedCoin.symbol}
                    </span>
                    <span className="px-2 py-0.5 bg-white/5 text-white/50 text-[10px] rounded uppercase font-mono">
                      {selectedCoin.source}
                    </span>
                    {getMultiplierVal(selectedCoin) > 0 && (
                      <span className="px-2 py-0.5 bg-red-500/20 border border-red-500/40 text-red-400 text-[10px] font-black rounded flex items-center gap-0.5 shadow-[0_0_10px_rgba(239,68,68,0.2)]">
                        🔥 {getMultiplierVal(selectedCoin).toFixed(1)}x Peak
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-center sm:justify-start gap-2 font-mono text-[11px] text-white/40">
                    <span className="truncate max-w-[180px] sm:max-w-none">{selectedCoin.mint}</span>
                    <button
                      onClick={() => handleCopy(selectedCoin.mint)}
                      className="hover:text-white transition-colors"
                      title="Copy Address"
                    >
                      {copied ? <Check className="size-3.5 text-emerald-400" /> : <Copy className="size-3.5" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Price / Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 font-mono text-center">
                <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3">
                  <div className="text-[9px] text-white/40 uppercase tracking-wider mb-1">{t("search.priceUsd")}</div>
                  <div className="text-sm font-bold text-white">
                    {selectedCoin.priceUsd ? `$${selectedCoin.priceUsd < 0.001 ? selectedCoin.priceUsd.toExponential(3) : selectedCoin.priceUsd.toFixed(5)}` : "—"}
                  </div>
                </div>
                <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3">
                  <div className="text-[9px] text-white/40 uppercase tracking-wider mb-1">Market Cap</div>
                  <div className="text-sm font-bold text-emerald-400">
                    {selectedCoin.mcap ? `$${selectedCoin.mcap.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}
                  </div>
                </div>
                <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3">
                  <div className="text-[9px] text-white/40 uppercase tracking-wider mb-1">{t("search.liquidity")}</div>
                  <div className="text-sm font-bold text-white">
                    {isLoadingMeta ? (
                      <Loader2 className="size-3.5 animate-spin mx-auto text-emerald-400" />
                    ) : coinMeta?.liquidityUsd ? (
                      `$${coinMeta.liquidityUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                    ) : "—"}
                  </div>
                </div>
                <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3">
                  <div className="text-[9px] text-white/40 uppercase tracking-wider mb-1">Volume (24h)</div>
                  <div className="text-sm font-bold text-white">
                    {isLoadingMeta ? (
                      <Loader2 className="size-3.5 animate-spin mx-auto text-emerald-400" />
                    ) : coinMeta?.volumeUsd24h ? (
                      `$${coinMeta.volumeUsd24h.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                    ) : "—"}
                  </div>
                </div>
                <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3 col-span-2 sm:col-span-1">
                  <div className="text-[9px] text-white/40 uppercase tracking-wider mb-1">Multiplier</div>
                  <div className="text-sm font-bold text-red-400">
                    {getMultiplierVal(selectedCoin) > 0 ? `${getMultiplierVal(selectedCoin).toFixed(2)}x` : "—"}
                  </div>
                </div>
              </div>

              {/* Description Panel */}
              <div className="space-y-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-white/40">{t("search.description")}</h3>
                <div className="bg-black/30 border border-white/5 rounded-xl p-3.5 text-sm text-white/90 leading-relaxed font-sans max-h-36 overflow-y-auto custom-scrollbar">
                  {isLoadingMeta && !selectedCoin.description ? (
                    <div className="flex items-center gap-2 text-white/40">
                      <Loader2 className="size-3.5 animate-spin text-emerald-400" />
                      <span>Fetching metadata...</span>
                    </div>
                  ) : (coinMeta?.description || selectedCoin.description) ? (
                    coinMeta?.description || selectedCoin.description
                  ) : (
                    <span className="italic text-white/30 text-xs">No description available for this token.</span>
                  )}
                </div>
              </div>

              {/* Metadata Details & Socials */}
              <div className="flex flex-col sm:flex-row gap-4 items-stretch justify-between">
                {/* Developer details */}
                <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3 flex-1 flex flex-col justify-center">
                  <span className="text-[9px] font-mono text-white/45 uppercase tracking-wider">Creator Address</span>
                  <div className="flex items-center gap-2 mt-1.5 font-mono text-xs text-white/80">
                    {isLoadingMeta ? (
                      <Loader2 className="size-3 animate-spin text-emerald-400" />
                    ) : coinMeta?.creator ? (
                      <>
                        <span className="truncate max-w-[120px] sm:max-w-xs">{coinMeta.creator}</span>
                        <button
                          onClick={() => handleCopy(coinMeta.creator || "")}
                          className="text-white/40 hover:text-white transition-colors"
                          title="Copy Address"
                        >
                          <Copy className="size-3" />
                        </button>
                      </>
                    ) : (
                      <span className="italic text-white/30">—</span>
                    )}
                  </div>
                </div>

                {/* Social buttons */}
                <div className="flex items-center gap-2 shrink-0 justify-center sm:justify-start">
                  {coinMeta?.website && (
                    <a
                      href={coinMeta.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2.5 rounded-xl bg-white/[0.03] border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all text-white/80 hover:text-white"
                      title="Website"
                    >
                      <Globe className="size-4.5" />
                    </a>
                  )}
                  {coinMeta?.twitter && (
                    <a
                      href={coinMeta.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2.5 rounded-xl bg-white/[0.03] border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all text-white/80 hover:text-white"
                      title="Twitter"
                    >
                      <Twitter className="size-4.5" />
                    </a>
                  )}
                  {coinMeta?.telegram && (
                    <a
                      href={coinMeta.telegram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2.5 rounded-xl bg-white/[0.03] border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all text-white/80 hover:text-white"
                      title="Telegram"
                    >
                      <MessageCircle className="size-4.5" />
                    </a>
                  )}
                  {coinMeta?.dexUrl && (
                    <a
                      href={coinMeta.dexUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2.5 rounded-xl bg-white/[0.03] border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all text-white/80 hover:text-white"
                      title="DexScreener Link"
                    >
                      <ExternalLink className="size-4.5" />
                    </a>
                  )}
                </div>
              </div>

              {/* Modal action bar */}
              <div className="flex gap-2.5 pt-2">
                <button
                  onClick={() => setSelectedCoin(null)}
                  className="px-4 py-2 text-xs font-bold font-mono border border-white/10 rounded-xl hover:bg-white/5 text-white/70 hover:text-white transition-all shrink-0"
                >
                  Back
                </button>
                <button
                  onClick={() => handleOpen(selectedCoin.mint, "/app")}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-black uppercase text-xs tracking-wider transition-all shadow-[0_0_15px_rgba(16,185,129,0.3)] active:scale-95"
                >
                  <span>Workspace</span>
                  <ExternalLink className="size-3.5" />
                </button>
                <button
                  onClick={() => handleOpen(selectedCoin.mint, "/deploy")}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-400 text-black font-black uppercase text-xs tracking-wider transition-all shadow-[0_0_15px_rgba(59,130,246,0.3)] active:scale-95"
                >
                  <span>Playground</span>
                  <ExternalLink className="size-3.5" />
                </button>
              </div>

            </div>
          )}
        </div>

      </div>
    </div>,
    document.body
  );
}
